import type { FieldType } from '@servicetim/shared';
import type { ComponentType } from 'react';
import { BooleanControl } from './controls/boolean-control';
import { DateControl, DateTimeControl } from './controls/date-controls';
import { MultiSelectControl, SingleSelectControl } from './controls/select-controls';
import { TagsControl } from './controls/tags-control';
import { LongTextControl, NumberControl, TextControl } from './controls/text-controls';
import { TicketLookupControl } from './controls/ticket-picker';
import { MultiUserPickerControl, UserPickerControl } from './controls/user-picker';
import type { Pickers, SchemaFormField } from './types';

export interface FieldControlProps {
  field: SchemaFormField;
  value: unknown;
  /** `undefined` clears the value (the form decides whether that becomes "omit" or `null`). */
  onChange: (value: unknown) => void;
  onBlur?: () => void;
  /** Input id the row's `<label htmlFor>` points at. */
  id: string;
  disabled?: boolean;
  invalid?: boolean;
  /** id(s) of the description / error elements. */
  describedBy?: string;
  /** Label for an id-valued current value (user, ticket) from the API's `display` map. */
  displayLabel?: string;
  pickers?: Pickers;
}

/** One control per registry type (ADR-0016 §3). Add a type here when the registry grows. */
export const FIELD_CONTROLS: Record<FieldType, ComponentType<FieldControlProps>> = {
  text: TextControl,
  long_text: LongTextControl,
  number: NumberControl,
  date: DateControl,
  datetime: DateTimeControl,
  boolean: BooleanControl,
  single_select: SingleSelectControl,
  multi_select: MultiSelectControl,
  user: UserPickerControl,
  multi_user: MultiUserPickerControl,
  tags: TagsControl,
  url: TextControl,
  email: TextControl,
  phone: TextControl,
  ticket_lookup: TicketLookupControl,
};

/** Renders the control for `props.field.type`. */
export function FieldControl(props: FieldControlProps) {
  const Control = FIELD_CONTROLS[props.field.type];
  return <Control {...props} />;
}
